import React from 'react';
import { useTranslation } from 'react-i18next';
import { GlobalSetting } from 'interfaces/globalSetting.interface';
import { UUID, CommonUtilsService, WeekDays, WeekDaysButtons, CommonGlobalService } from '@umanforge/common';
import ToggleSwitch from '@common_components/inputs/toggleSwitch.component';
import InputText from '@common_components/inputs/textInput.component';
import { KEYFRAME } from 'constants/setting.constants';

interface SettingsClientProps {
  clientId: UUID | undefined;
  groupedSettings: { calendar: GlobalSetting[]; eventManager: GlobalSetting[] };
  setGroupedSettings: ( settings: { calendar: GlobalSetting[]; eventManager: GlobalSetting[] } ) => void;
  onSave: () => Promise<void>;
  isSaving: boolean;
  canSave: boolean;
}

const SettingsClient: React.FC<SettingsClientProps> = ( {
  groupedSettings,
  setGroupedSettings,
  onSave,
  isSaving,
  canSave
} ) => {
  const { t } = useTranslation();

  const handleChange = ( group: keyof typeof groupedSettings, index: number, value: string | number | boolean ) => {
    setGroupedSettings( {
      ...groupedSettings,
      [ group ]: [
        ...groupedSettings[ group ].slice( 0, index ),
        { ...groupedSettings[ group ][ index ], value },
        ...groupedSettings[ group ].slice( index + 1 ),
      ],
    } );
  };

  const handleSelectionChange = ( selectedDays: WeekDays ) => {
    const binaryValue = CommonGlobalService.getBinaryFromDays( selectedDays );
    const group = 'eventManager';
    const index = groupedSettings[ group ].findIndex(
      setting => {return setting.keyframe === KEYFRAME.DAYS_DISPLAY;}
    );

    if ( index !== -1 ) {
      handleChange( group as keyof typeof groupedSettings, index, binaryValue );
    }
  };

  const handleSave = async () => {
    await onSave();
  };

  return (
    <div className="flex flex-col gap-6">
      {Object.entries( groupedSettings )
        .filter( ( [ , settings ] ) => {return settings.length > 0;} )
        .map( ( [ groupName, settings ] ) => {return (
          <div key={groupName} className="p-6 rounded-xl border bg-white">
            <h2 className="text-xl font-semibold mb-4 text-gray-700">
              {t( CommonUtilsService.camelToSnake( groupName ) )}
            </h2>
            <div className="flex flex-col gap-4">
              {settings.map( ( setting, index ) => {return (
                <div key={setting.keyframe} className="flex flex-col gap-2">
                  { setting.keyframe === KEYFRAME.REFESH_DELAY_VIEW_MODE ? (
                    <InputText
                      label={`${t( 'translation:settings.' + setting.keyframe )} (${t( 'common:in_minutes' )})`}
                      type="number"
                      textSize="w-5/6"
                      value={Number( setting.value )}
                      onChange={( value: string ) => {return handleChange( groupName as keyof typeof groupedSettings, index, value );}}
                    />
                  ) : setting.keyframe === KEYFRAME.CALENDAR_COLUMN ? (
                    <InputText
                      label={`${t( 'translation:settings.' + setting.keyframe )} (${t( 'common:in_days' )})`}
                      type="number"
                      textSize="w-5/6"
                      value={Number( setting.value )}
                      onChange={( value: string ) => {return handleChange( groupName as keyof typeof groupedSettings, index, value );}}
                    />
                  ) : setting.keyframe === KEYFRAME.DAYS_DISPLAY ? (
                    <WeekDaysButtons
                      textSize="w-5/6"
                      label={t( 'translation:settings.' + setting.keyframe )}
                      defaultSelectedDays={CommonGlobalService.getDaysFromBinary( Number( setting.value ) )}
                      onSelectionChange={handleSelectionChange}
                    />
                  ) : setting.keyframe === KEYFRAME.CAN_CREATE_FROM_BOARD ? (
                    <div className="flex items-center justify-between">
                      <ToggleSwitch
                        label={t( 'translation:settings.' + setting.keyframe )}
                        isActive={setting.value === '1'}
                        onToggle={() => {return handleChange( groupName as keyof typeof groupedSettings, index, setting.value === '1' ? '0' : '1' );}}
                      />
                    </div>
                  ) : setting.type === 'boolean' ? (
                    <div className="flex items-center justify-between">
                      <ToggleSwitch
                        label={t( 'translation:settings.' + setting.keyframe )}
                        isActive={setting.value === '1'}
                        onToggle={() => {return handleChange( groupName as keyof typeof groupedSettings, index, setting.value === '1' ? '0' : '1' );}}
                      />
                    </div>
                  ) : (
                    <InputText
                      type="text"
                      label={t( 'translation:settings.' + setting.keyframe )}
                      value={String( setting.value )}
                      onChange={( value: string ) => {return handleChange( groupName as keyof typeof groupedSettings, index, value );}}
                    />
                  )}
                </div>
              );} )}
            </div>
          </div>
        );} )}
      <div className="flex justify-end">
        <button
          className="mt-6 mb-24 px-4 py-2 bg-blue-100 text-blue-600 rounded disabled:opacity-50 hover:bg-blue-200 trans-fast"
          onClick={handleSave}
          disabled={isSaving || !canSave}
        >
          {isSaving ? t( 'common:saving' ) : t( 'common:save' )}
        </button>
      </div>
    </div>
  );
};

export default SettingsClient;
